/**
 * PlanCard Component
 * Displays a single plan with price, features, and action button
 */
import Button from '@/components/ui/Button';
import FeatureList from './FeatureList';
import { cn } from '@/utils/cn';
import type { Plan, PlanInterval } from '@/types/billing';

export interface PlanCardProps {
  plan: Plan;
  isCurrentPlan?: boolean;
  isLoading?: boolean;
  discountedAmount?: number | null;
  /** WP-BILLING-11: True when a FREE_FOREVER coupon is applied */
  isFreeForever?: boolean;
  /** WP-BILLING-13: True when a FREE_MONTHS coupon is applied */
  isFreeMonths?: boolean;
  /** WP-BILLING-13: Display text for free months (e.g., "1 month free") */
  freeMonthsDisplay?: string | null;
  onSelect: () => void;
  className?: string;
}

/**
 * Format price for display
 * Converts cents to dollars and formats with currency symbol
 */
function formatPrice(amount: number, currency: string = 'USD'): string {
  const dollars = amount / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(dollars);
}

/**
 * Format interval for display
 */
function formatInterval(interval: PlanInterval): string {
  return interval === 'monthly' ? '/month' : '/year';
}

export default function PlanCard({
  plan,
  isCurrentPlan = false,
  isLoading = false,
  discountedAmount,
  isFreeForever = false,
  isFreeMonths = false,
  freeMonthsDisplay,
  onSelect,
  className,
}: PlanCardProps) {
  const hasDiscount = discountedAmount !== null && discountedAmount !== undefined;
  const displayAmount = hasDiscount ? discountedAmount : plan.amount;
  const originalAmount = hasDiscount ? plan.amount : null;

  // WP-BILLING-13: Check if any free trial coupon is applied
  const hasFreeTrialCoupon = isFreeForever || isFreeMonths;

  // Determine button text and variant
  // WP-BILLING-11: Show "Activate Premium" for FREE_FOREVER coupons
  // WP-BILLING-13: Show "Start Free Trial" for FREE_MONTHS coupons
  const getButtonText = () => {
    if (isCurrentPlan) return 'Current Plan';
    if (isFreeForever) return 'Activate Premium';
    if (isFreeMonths) return 'Start Free Trial';
    if (plan.trialPeriodDays > 0) return `Start ${plan.trialPeriodDays}-day trial`;
    return 'Get Started';
  };

  const getButtonVariant = (): 'primary' | 'secondary' | 'outline' => {
    if (isCurrentPlan) return 'secondary';
    // WP-BILLING-11/13: Use primary variant for free coupons to draw attention
    if (hasFreeTrialCoupon || plan.isPopular) return 'primary';
    return 'outline';
  };

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-xl border p-6 h-full',
        isCurrentPlan
          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500'
          : plan.isPopular
          ? 'border-primary-200 bg-white'
          : 'border-gray-200 bg-white',
        className
      )}
    >
      {/* Badges */}
      {plan.isPopular && !isCurrentPlan && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-600 text-white text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
          Most Popular
        </span>
      )}
      {isCurrentPlan && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-600 text-white text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
          Current Plan
        </span>
      )}

      {/* Plan name and description */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
        {plan.description && (
          <p className="mt-1 text-sm text-gray-500">{plan.description}</p>
        )}
      </div>

      {/* Price */}
      <div className="mb-4">
        <div className="flex items-baseline gap-2">
          <span className={cn(
            'text-3xl font-bold',
            isFreeForever ? 'text-purple-600' : isFreeMonths ? 'text-blue-600' : 'text-gray-900'
          )}>
            {hasFreeTrialCoupon ? 'FREE' : formatPrice(displayAmount, plan.currency)}
          </span>
          {!hasFreeTrialCoupon && (
            <span className="text-sm text-gray-500">
              {formatInterval(plan.interval)}
            </span>
          )}
          {isFreeForever && (
            <span className="text-sm font-medium text-purple-600">
              forever
            </span>
          )}
          {isFreeMonths && (
            <span className="text-sm font-medium text-blue-600">
              {freeMonthsDisplay || 'trial period'}
            </span>
          )}
        </div>
        {originalAmount !== null && !hasFreeTrialCoupon && (
          <div className="mt-1">
            <span className="text-sm text-gray-400 line-through">
              {formatPrice(originalAmount, plan.currency)}
            </span>
            <span className="ml-2 text-sm font-medium text-green-600">
              Save {formatPrice(originalAmount - displayAmount, plan.currency)}
            </span>
          </div>
        )}
        {isFreeForever && (
          <div className="mt-1">
            <span className="text-sm text-gray-400 line-through">
              {formatPrice(plan.amount, plan.currency)}{formatInterval(plan.interval)}
            </span>
            <span className="ml-2 text-sm font-medium text-purple-600">
              100% off - no payment required
            </span>
          </div>
        )}
        {isFreeMonths && (
          <div className="mt-1">
            <span className="text-sm text-gray-400 line-through">
              {formatPrice(plan.amount, plan.currency)}{formatInterval(plan.interval)}
            </span>
            <span className="ml-2 text-sm font-medium text-blue-600">
              then {formatPrice(plan.amount, plan.currency)}{formatInterval(plan.interval)}
            </span>
          </div>
        )}
        {plan.trialPeriodDays > 0 && !isCurrentPlan && !hasFreeTrialCoupon && (
          <p className="mt-1 text-sm text-blue-600">
            {plan.trialPeriodDays}-day free trial included
          </p>
        )}
      </div>

      {/* Features */}
      <div className="flex-1 mb-6">
        <FeatureList features={plan.features} />
      </div>

      {/* Action button */}
      <Button
        variant={getButtonVariant()}
        className="w-full"
        disabled={isCurrentPlan}
        isLoading={isLoading}
        onClick={onSelect}
        aria-label={isCurrentPlan ? 'This is your current plan' : `Select ${plan.name} plan`}
      >
        {getButtonText()}
      </Button>
    </div>
  );
}
